/**
 * `Push All Ready To TMS` - the Technical Officials panel's head action (the TD's request of
 * 2 October 2026: sixteen technical-panel officials still to push, the tournament ending the next
 * day, ONE action instead of sixteen).
 *
 * **What "ready" means, and who decides it.** The API's ready list (`GET /reports/push/ready`):
 * a TO, Judge or ATD whose report is `TD Drafted` (stage 2) and who passes every gate of the single
 * push's prepare - every mark set (an `N/A` is held back: TMS's form has no box to carry it), the
 * text within TMS's 800. An official at `TMS Draft` or beyond is in TMS already, and is never
 * listed, so never queued again. The same function decides the list and the batch prepare, so the
 * figure on the button is the figure that will be queued.
 *
 * **Nothing happens before the TD has seen the count.** The button names how many it will queue,
 * and the line beneath it names every one of them, and every official held back with why, BEFORE
 * it is pressed. Pressing it records one ordinary press per official and hands them to the TMS Push
 * extension, whose own window lists them all and sends nothing until the TD's Confirm there.
 *
 * **What it says afterwards** is what the API recorded, official by official
 * (`reportBatchFlow.ts`): saved only where the API accepted a read-back that is the plan; every
 * other official in red, with why. The single official's `Push To TMS` on each profile is untouched.
 */

import { useCallback, useEffect, useState } from "react";

import { fetchReportPushReady } from "../../api/client.js";
import type { ReportPushReadyBody } from "../../api/types.js";
import { pushReportBatchThroughExtension, type ReportBatchOutcome } from "../marking/reportBatchFlow.js";

export interface ReportBatchController {
  readonly ready: ReportPushReadyBody | null;
  readonly readError: string | null;
  readonly running: boolean;
  readonly progress: string | null;
  readonly outcome: ReportBatchOutcome | null;
  readonly failure: string | null;
  readonly start: () => void;
}

export interface ReportBatchDeps {
  readonly fetchReady: () => Promise<ReportPushReadyBody>;
  readonly pushBatch: typeof pushReportBatchThroughExtension;
}

const LIVE: ReportBatchDeps = { fetchReady: fetchReportPushReady, pushBatch: pushReportBatchThroughExtension };

/**
 * The batch's state, shared by the head button and the report beneath it. `onFinished` runs once a
 * batch has ended - whatever its outcome - so the page can re-read its pills.
 */
export function useReportBatch(onFinished: () => void, deps: ReportBatchDeps = LIVE): ReportBatchController {
  const [ready, setReady] = useState<ReportPushReadyBody | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [reads, setReads] = useState(0);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<ReportBatchOutcome | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    deps.fetchReady().then(
      (body) => {
        if (!live) return;
        setReady(body);
        setReadError(null);
      },
      (error: unknown) => {
        if (live) setReadError(error instanceof Error ? error.message : "The ready list could not be read.");
      },
    );
    return () => {
      live = false;
    };
    // `deps` is fixed for the page's life; `reads` asks again after a batch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reads]);

  const start = useCallback(() => {
    if (running || ready === null || ready.ready.length === 0) return;
    setRunning(true);
    setOutcome(null);
    setFailure(null);
    setProgress("Asking the TMS Push extension…");
    deps.pushBatch(ready.ready, setProgress).then(
      (ended) => {
        setOutcome(ended);
      },
      (error: unknown) => {
        setFailure(error instanceof Error ? error.message : "The batch could not be sent.");
      },
    ).finally(() => {
      setRunning(false);
      setProgress(null);
      setReads((count) => count + 1);
      onFinished();
    });
  }, [running, ready, deps, onFinished]);

  return { ready, readError, running, progress, outcome, failure, start };
}

/** `Push All 16 Ready To TMS` - the count stated before anything happens. */
export function pushAllLabel(count: number): string {
  if (count === 0) return "No Reports Ready To Push";
  return `Push All ${String(count)} Ready To TMS`;
}

/** The head action: the button, stating its count. */
export function PushAllReadyButton({ controller }: { readonly controller: ReportBatchController }): React.JSX.Element {
  const count = controller.ready?.ready.length ?? 0;
  return (
    <button
      type="button"
      className="gbtn gbtn--solid"
      data-testid="push-all-ready"
      disabled={controller.ready === null || count === 0 || controller.running}
      title={
        count === 0
          ? "No technical official is TD Drafted with every mark set."
          : `Queues ${String(count)} TD Drafted ${count === 1 ? "report" : "reports"} with ${count === 1 ? "its" : "their"} marks. The TMS Push window lists every one before anything is sent.`
      }
      onClick={controller.start}
    >
      {controller.ready === null ? "Push All Ready To TMS" : pushAllLabel(count)}
    </button>
  );
}

/** Beneath the head: who would be queued, who is held back, the run's progress, and its outcome. */
export function PushAllReadyReport({ controller }: { readonly controller: ReportBatchController }): React.JSX.Element | null {
  const { ready, readError, progress, outcome, failure } = controller;
  if (ready === null && readError === null && outcome === null && failure === null) return null;
  return (
    <div className="reportbatch" data-testid="push-all-report">
      {readError === null ? null : (
        <p className="btnhint" data-testid="push-all-read-error">
          The ready list could not be read ({readError}). Push each official from their own profile.
        </p>
      )}
      {ready === null || progress !== null || outcome !== null ? null : (
        <p className="btnhint" data-testid="push-all-ready-list">
          {ready.ready.length === 0
            ? "Nothing to queue: no TO, Judge or ATD is TD Drafted with every mark set."
            : `Will queue ${String(ready.ready.length)}: ${ready.ready.map((entry) => `${entry.mediaName} (${entry.role})`).join(", ")}.`}
          {ready.held.length === 0 ? null : (
            <>
              {" "}
              <b>Held back {String(ready.held.length)}:</b>{" "}
              {ready.held.map((entry) => `${entry.mediaName} (${entry.role}) - ${entry.reason}`).join(" ")}
            </>
          )}
        </p>
      )}
      {progress === null ? null : (
        <div className="reportpush-progress" role="status" aria-live="polite" data-testid="push-all-progress">
          {progress}
        </div>
      )}
      {failure === null ? null : (
        <div className="writeerror" role="alert" data-testid="push-all-failure">
          <b>Nothing was pushed —</b> {failure}
        </div>
      )}
      {outcome === null ? null : (
        <div className={outcome.failed === 0 ? "reportbatch__done" : "writeerror"} role={outcome.failed === 0 ? "status" : "alert"} data-testid="push-all-outcome">
          <b>{outcome.headline}</b>
          <ul>
            {outcome.lines.map((line) => (
              <li key={line.tmsPersonId} className={line.saved ? "reportbatch__saved" : "reportbatch__failed"} data-testid={`push-all-line-${line.tmsPersonId}`}>
                <b>{line.who}:</b> {line.words}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
